import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LuArrowRight, LuMenu, LuX } from "react-icons/lu";
import { sectionPath } from "../routes";
import { ButtonLink, Container } from "./ui";

const navItems = [
  { label: "Services", id: "services" },
  { label: "AI & Data", id: "ai-data" },
  { label: "Work", id: "portfolio" },
  { label: "About", id: "about" },
  { label: "Tech stack", id: "skills" },
];

const Header = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  height: var(--nav-height);
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: saturate(180%) blur(12px);
  -webkit-backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid
    ${({ $scrolled }) => ($scrolled ? "var(--color-border)" : "transparent")};
  transition: border-color 0.2s ease;
`;

const Bar = styled(Container)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 34px;
    width: auto;
    mix-blend-mode: multiply;
  }
`;

const Links = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 960px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ $active }) =>
    $active ? "var(--color-primary)" : "var(--color-text)"};
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: ${({ $active }) =>
      $active ? "var(--color-primary)" : "var(--color-ink)"};
    background: var(--color-bg-subtle);
  }
`;

const Cta = styled(ButtonLink)`
  min-height: 42px;
  padding: 0 18px;

  @media (max-width: 960px) {
    display: none;
  }
`;

const MenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  color: var(--color-ink);

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 960px) {
    display: inline-flex;
  }
`;

const MobilePanel = styled(motion.div)`
  position: fixed;
  top: var(--nav-height);
  left: 0;
  right: 0;
  z-index: 99;
  padding: 8px 16px 24px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);

  @media (min-width: 961px) {
    display: none;
  }
`;

const MobileLink = styled(Link)`
  display: block;
  padding: 14px 4px;
  border-bottom: 1px solid var(--color-border);
  font-family: var(--font-heading);
  font-size: 1.125rem;
  font-weight: 500;
  color: ${({ $active }) =>
    $active ? "var(--color-primary)" : "var(--color-ink)"};
`;

const MobileCta = styled(ButtonLink)`
  width: 100%;
  margin-top: 20px;
`;

const Navigation = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Highlight the nav item for the section currently in view (home page only).
  useEffect(() => {
    setActiveId(null);
    setMenuOpen(false);
    if (pathname !== "/") return undefined;

    const sections = navItems
      .map(item => document.getElementById(item.id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKey = e => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <Header $scrolled={scrolled || menuOpen}>
        <Bar>
          <Brand to="/" aria-label="Code Solutions — home">
            <img src="/LOGO.png" alt="Code Solutions" width="105" height="34" />
          </Brand>

          <Links aria-label="Main">
            {navItems.map(item => (
              <NavLink
                key={item.id}
                to={sectionPath(item.id)}
                $active={activeId === item.id}
                aria-current={activeId === item.id ? "true" : undefined}
              >
                {item.label}
              </NavLink>
            ))}
          </Links>

          <Cta as={Link} to={sectionPath("contact")}>
            Start a project
            <LuArrowRight data-arrow aria-hidden="true" />
          </Cta>

          <MenuButton
            type="button"
            onClick={() => setMenuOpen(open => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <LuX aria-hidden="true" /> : <LuMenu aria-hidden="true" />}
          </MenuButton>
        </Bar>
      </Header>

      <AnimatePresence>
        {menuOpen && (
          <MobilePanel
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <nav aria-label="Mobile">
              {navItems.map(item => (
                <MobileLink
                  key={item.id}
                  to={sectionPath(item.id)}
                  onClick={closeMenu}
                  $active={activeId === item.id}
                >
                  {item.label}
                </MobileLink>
              ))}
            </nav>
            <MobileCta as={Link} to={sectionPath("contact")} onClick={closeMenu}>
              Start a project
              <LuArrowRight data-arrow aria-hidden="true" />
            </MobileCta>
          </MobilePanel>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
